import { Form } from "@inertiajs/react"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { adminActAsBrandPath } from "@/routes"

/**
 * OKS-312 — App-wide "Acting as {Brand}" banner.
 *
 * Renders ONLY when `useCurrentContext().actingAs` is set (i.e. an OKS
 * Admin is operating inside the "act as brand" context). Returns `null`
 * for every other request. Mounted in `app-layout.tsx` ABOVE the role
 * shell so it persists across `/admin/*`, `/brand/*`, `/agency/*`, and
 * `/missions` navigation (UX Spec §2.2 + PRD A3).
 *
 * # Layout
 *   - Desktop (≥640px): single 40px row — `[dot] Acting as [brand] — exit when done` (left) + Exit (right).
 *   - Mobile (<640px): stacks to 56px — `[dot] Acting as [brand]` (top) + Exit (bottom).
 *
 * # Colour
 *   - When `brand_color` is non-null, the strip uses it (future hook —
 *     `brand_color` is reserved per UX Spec §4 and ships `null` today).
 *   - When `brand_color` is null, fallback to the amber attention strip
 *     (`bg-amber-100 text-amber-900 border-amber-200`).
 *
 * # Exit
 *   - Inertia `<Form method="delete">` POST-emulated to the
 *     `adminActAsBrandPath()` endpoint. CSRF via Rails meta tag (Crew
 *     hard rule — no `<form>` token plumbing in component code).
 *
 * # Accessibility
 *   - Outer wrap: `role="status"` `aria-live="polite"` so AT announces
 *     the context on render. Colour is not the sole indicator — the
 *     textual "Acting as" label carries the meaning.
 *   - Exit button: `aria-label` carries the full "Exit acting as
 *     {Brand}" so AT users get a clean standalone announcement.
 *   - Long brand names truncate to 24 chars in the visible label; the
 *     full name is preserved in `aria-label` + the title tooltip.
 */
const BRAND_NAME_MAX_CHARS = 24

export function ActingAsBanner() {
  const { actingAs } = useCurrentContext()
  const { t } = useTranslation()

  if (!actingAs) {
    return null
  }

  const fullName = actingAs.brand_name
  const truncatedName =
    fullName.length > BRAND_NAME_MAX_CHARS
      ? `${fullName.slice(0, BRAND_NAME_MAX_CHARS - 1)}…`
      : fullName

  // brand_color is reserved (always null today); fall back to amber.
  // Once a brand-level accent column lands the inline-style branch
  // here picks it up automatically.
  const inlineStyle = actingAs.brand_color
    ? { backgroundColor: actingAs.brand_color }
    : undefined
  const colorClasses = actingAs.brand_color
    ? "border-b text-white"
    : "border-b border-amber-200 bg-amber-100 text-amber-900"

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${t("admin.act_as.banner.label")} ${fullName}`}
      data-testid="acting-as-banner"
      data-brand-id={actingAs.brand_id}
      className={`relative z-30 w-full ${colorClasses}`}
      style={inlineStyle}
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-4 py-2 sm:h-10 sm:flex-row sm:items-center sm:gap-3 sm:py-0">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            aria-hidden="true"
            data-testid="acting-as-banner-dot"
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-900/15 text-xs font-semibold uppercase"
          >
            {actingAs.brand_initial}
          </span>
          <span className="text-sm opacity-80">
            {t("admin.act_as.banner.label")}
          </span>
          <span
            className="truncate text-sm font-semibold"
            title={fullName}
            data-testid="acting-as-banner-brand-name"
          >
            {truncatedName}
          </span>
          <span className="hidden text-xs opacity-70 sm:inline">
            {t("admin.act_as.banner.suffix")}
          </span>
        </div>
        <Form
          action={adminActAsBrandPath()}
          method="delete"
          className="flex shrink-0 justify-end"
        >
          {({ processing }) => (
            <Button
              type="submit"
              variant="ghost"
              size="sm"
              disabled={processing}
              aria-label={t("admin.act_as.banner.exit_aria", {
                brand: fullName,
              })}
              data-testid="acting-as-banner-exit"
              className="min-h-[44px] gap-1 sm:min-h-0"
            >
              <X aria-hidden="true" className="size-4" />
              {t("admin.act_as.banner.exit")}
            </Button>
          )}
        </Form>
      </div>
    </div>
  )
}
